import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus, MoreHorizontal, ArrowLeft, Sparkles, Paperclip, Trash2, Pencil,
  Upload, FileAudio, FileText, Image, Copy, GripVertical, Mic, Check,
  X, MessageSquare, FileUp, Loader2, CheckCircle2, AlertCircle, Brain
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  high: "priority-high",
  medium: "priority-medium",
  low: "priority-low"
};

const PRIORITY_LABELS = {
  high: "Alta",
  medium: "Média",
  low: "Baixa"
};

export default function KanbanPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [project, setProject] = useState(null);
  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "", description: "", column_id: "", priority: "medium", due_date: ""
  });
  const [draggedTask, setDraggedTask] = useState(null);
  
  // AI Assistant State
  const [aiStep, setAiStep] = useState("upload"); // upload, processing, review
  const [pendingFiles, setPendingFiles] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [processingStatus, setProcessingStatus] = useState({ current: 0, total: 0, message: "" });
  const [aiResult, setAiResult] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);
  
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, columnsRes, tasksRes, filesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/columns?project_id=${projectId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/tasks?project_id=${projectId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/files?project_id=${projectId}`, { headers: getAuthHeaders() })
      ]);
      setProject(projectRes.data);
      setColumns(columnsRes.data);
      setTasks(tasksRes.data);
      setFiles(filesRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 404) {
        navigate("/clients");
      }
      toast.error("Erro ao carregar projeto");
    } finally {
      setLoading(false);
    }
  }, [projectId, getAuthHeaders, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim() || !taskForm.column_id) {
      toast.error("Preencha título e coluna");
      return;
    }

    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, taskForm, { headers: getAuthHeaders() });
        toast.success("Tarefa atualizada!");
      } else {
        await axios.post(`${API}/tasks`, { ...taskForm, project_id: projectId }, { headers: getAuthHeaders() });
        toast.success("Tarefa criada!");
      }
      setTaskDialogOpen(false);
      setEditingTask(null);
      setTaskForm({ title: "", description: "", column_id: "", priority: "medium", due_date: "" });
      fetchData();
    } catch (error) {
      console.error("Save task error:", error);
      toast.error("Erro ao salvar tarefa");
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || "",
      column_id: task.column_id,
      priority: task.priority,
      due_date: task.due_date || ""
    });
    setTaskDialogOpen(true);
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { headers: getAuthHeaders() });
      toast.success("Tarefa excluída!");
      fetchData();
    } catch (error) {
      console.error("Delete task error:", error);
      toast.error("Erro ao excluir tarefa");
    }
  };

  // AI Assistant Functions
  const handleFilesSelected = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const newFiles = selectedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      type: getFileType(file),
      status: "pending", // pending, uploading, transcribing, ready, error
      transcription: null,
      uploaded: false
    }));

    setPendingFiles(prev => [...prev, ...newFiles]);
  };

  const getFileType = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (['mp3', 'wav', 'm4a', 'ogg', 'opus', 'oga', 'webm', 'mpeg', 'mpga', 'flac', 'aac', 'wma', 'amr'].includes(ext)) return "audio";
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return "image";
    return "document";
  };

  const removeFile = (fileId) => {
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const processFilesAndAnalyze = async () => {
    if (pendingFiles.length === 0 && !additionalNotes.trim()) {
      toast.error("Adicione pelo menos um arquivo ou nota");
      return;
    }

    setAiStep("processing");
    const totalSteps = pendingFiles.length * 2 + 1; // upload + transcribe + analyze
    let currentStep = 0;

    try {
      const uploadedFileIds = [];
      const transcriptions = [];

      // Step 1: Upload all files
      for (const pendingFile of pendingFiles) {
        setProcessingStatus({
          current: ++currentStep,
          total: totalSteps,
          message: `Enviando ${pendingFile.name}...`
        });

        const formData = new FormData();
        formData.append("file", pendingFile.file);
        formData.append("project_id", projectId);

        try {
          const uploadRes = await axios.post(`${API}/files/upload`, formData, {
            headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
          });
          
          uploadedFileIds.push(uploadRes.data.id);
          pendingFile.uploadedId = uploadRes.data.id;
          pendingFile.status = "uploaded";

          // Step 2: Transcribe audio files
          if (pendingFile.type === "audio") {
            setProcessingStatus({
              current: ++currentStep,
              total: totalSteps,
              message: `Transcrevendo ${pendingFile.name}...`
            });

            try {
              const transcribeRes = await axios.post(
                `${API}/ai/transcribe/${uploadRes.data.id}`,
                {},
                { headers: getAuthHeaders() }
              );
              transcriptions.push({
                filename: pendingFile.name,
                text: transcribeRes.data.transcription
              });
              pendingFile.transcription = transcribeRes.data.transcription;
              pendingFile.status = "transcribed";
            } catch (err) {
              console.error("Transcription error:", err);
              pendingFile.status = "error";
              currentStep++; // Skip the transcription step count
            }
          } else {
            currentStep++; // Skip transcription for non-audio
          }
        } catch (err) {
          console.error("Upload error:", err);
          pendingFile.status = "error";
          currentStep += 2; // Skip both upload and transcription
        }
      }

      // Step 3: Analyze with AI
      setProcessingStatus({
        current: ++currentStep,
        total: totalSteps,
        message: "Analisando conteúdo com IA..."
      });

      // Build content for AI analysis
      let fullContent = "";
      
      if (additionalNotes.trim()) {
        fullContent += `NOTAS E OBSERVAÇÕES:\n${additionalNotes}\n\n`;
      }

      for (const t of transcriptions) {
        fullContent += `TRANSCRIÇÃO DE "${t.filename}":\n${t.text}\n\n`;
      }

      // Get existing transcriptions from project files
      const existingTranscribedFiles = files.filter(f => f.transcription);
      for (const f of existingTranscribedFiles) {
        if (!uploadedFileIds.includes(f.id)) {
          fullContent += `TRANSCRIÇÃO ANTERIOR DE "${f.original_name}":\n${f.transcription}\n\n`;
        }
      }

      if (!fullContent.trim()) {
        toast.error("Nenhum conteúdo para analisar");
        setAiStep("upload");
        return;
      }

      const analyzeRes = await axios.post(`${API}/ai/organize`, {
        project_id: projectId,
        content: fullContent,
        file_ids: uploadedFileIds
      }, { headers: getAuthHeaders() });

      setAiResult(analyzeRes.data);
      setSelectedTasks(analyzeRes.data.suggested_tasks?.map((_, i) => i) || []);
      setAiStep("review");
      fetchData(); // Refresh files list

    } catch (error) {
      console.error("AI processing error:", error);
      toast.error("Erro ao processar com IA");
      setAiStep("upload");
    }
  };

  const toggleTaskSelection = (index) => {
    setSelectedTasks(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const createSelectedTasks = async () => {
    if (selectedTasks.length === 0) {
      toast.error("Selecione pelo menos uma tarefa");
      return;
    }

    const firstColumn = columns[0];
    let created = 0;

    for (const index of selectedTasks) {
      const task = aiResult.suggested_tasks[index];
      try {
        await axios.post(`${API}/tasks`, {
          title: task.title,
          description: task.description,
          priority: task.priority || "medium",
          project_id: projectId,
          column_id: firstColumn?.id || ""
        }, { headers: getAuthHeaders() });
        created++;
      } catch (err) {
        console.error("Create task error:", err);
      }
    }

    toast.success(`${created} tarefa(s) criada(s)!`);
    resetAiAssistant();
    fetchData();
  };

  const resetAiAssistant = () => {
    setAiStep("upload");
    setPendingFiles([]);
    setAdditionalNotes("");
    setAiResult(null);
    setSelectedTasks([]);
    setAiAssistantOpen(false);
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.column_id === columnId) {
      setDraggedTask(null);
      return;
    }

    try {
      await axios.put(`${API}/tasks/${draggedTask.id}`, { column_id: columnId }, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      console.error("Move task error:", error);
      toast.error("Erro ao mover tarefa");
    }
    setDraggedTask(null);
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/p/${project?.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const getFileIcon = (type) => {
    switch (type) {
      case "audio": return <FileAudio className="h-4 w-4" />;
      case "image": return <Image className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getFileStatusIcon = (status) => {
    switch (status) {
      case "uploading":
      case "transcribing":
        return <Loader2 className="h-4 w-4 animate-spin text-accent" />;
      case "uploaded":
      case "transcribed":
      case "ready":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <span className="text-muted-foreground">Carregando projeto...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="kanban-page">
      {/* Cover Image */}
      <div
        className="h-48 bg-cover bg-center relative shrink-0"
        style={{ backgroundImage: `url(${project?.cover_image})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60" />
        <div className="absolute top-4 left-4">
          <Link to={`/clients/${project?.client_id}/projects`}>
            <Button variant="secondary" size="sm" className="gap-2" data-testid="back-to-projects-btn">
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
          </Link>
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white heading-font">{project?.name}</h1>
            {project?.description && (
              <p className="text-white/80 mt-1 max-w-xl">{project.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={copyShareLink} data-testid="copy-share-link-btn">
              <Copy className="h-4 w-4 mr-2" />
              Compartilhar
            </Button>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="border-b px-4 py-3 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setTaskForm({ ...taskForm, column_id: columns[0]?.id || "" });
              setTaskDialogOpen(true);
            }}
            className="bg-gradient-to-r from-[#5AD4EB] to-[#00A7E1] text-[#004E8A] font-semibold hover:opacity-90"
            data-testid="new-task-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Tarefa
          </Button>
        </div>
        <Button
          onClick={() => setAiAssistantOpen(true)}
          className="gap-2 bg-gradient-to-r from-[#004E8A] to-[#00A7E1] text-white font-semibold hover:opacity-90"
          data-testid="ai-assistant-btn"
        >
          <Brain className="h-4 w-4" />
          Analisar Conteúdo com IA
        </Button>
      </div>

      {/* Kanban Board */}
      <ScrollArea className="flex-1">
        <div className="flex gap-6 p-6 min-h-full items-start">
          {columns.map((column) => (
            <div
              key={column.id}
              className="w-80 shrink-0 flex flex-col bg-muted/50 rounded-lg"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, column.id)}
              data-testid={`column-${column.id}`}
            >
              <div className="p-3 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{column.name}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {tasks.filter(t => t.column_id === column.id).length}
                  </Badge>
                </div>
              </div>
              <div className="p-3 space-y-3 min-h-[200px]">
                {tasks
                  .filter(t => t.column_id === column.id)
                  .map((task) => (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      className={`bg-background p-3 rounded-lg shadow-sm border border-border hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${draggedTask?.id === task.id ? "opacity-50" : ""}`}
                      data-testid={`task-${task.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium text-sm">{task.title}</h4>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" data-testid={`task-menu-${task.id}`}>
                                  <MoreHorizontal className="h-3 w-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditTask(task)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteTask(task.id)} className="text-destructive">
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>
                              {PRIORITY_LABELS[task.priority]}
                            </Badge>
                            {task.attachments?.length > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Paperclip className="h-3 w-3" />
                                {task.attachments.length}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                <Button
                  variant="ghost"
                  className="w-full justify-start text-muted-foreground"
                  onClick={() => {
                    setTaskForm({ ...taskForm, column_id: column.id });
                    setTaskDialogOpen(true);
                  }}
                  data-testid={`add-task-to-${column.id}`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar tarefa
                </Button>
              </div>
            </div>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={(open) => {
        setTaskDialogOpen(open);
        if (!open) {
          setEditingTask(null);
          setTaskForm({ title: "", description: "", column_id: "", priority: "medium", due_date: "" });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreateTask}>
            <DialogHeader>
              <DialogTitle className="heading-font">{editingTask ? "Editar Tarefa" : "Nova Tarefa"}</DialogTitle>
              <DialogDescription>
                {editingTask ? "Atualize os detalhes da tarefa" : "Adicione uma nova tarefa ao projeto"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Ex: Criar wireframe"
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Descreva a tarefa..."
                  data-testid="task-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Coluna *</Label>
                  <Select
                    value={taskForm.column_id}
                    onValueChange={(v) => setTaskForm({ ...taskForm, column_id: v })}
                  >
                    <SelectTrigger data-testid="task-column-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {columns.map(col => (
                        <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prioridade</Label>
                  <Select
                    value={taskForm.priority}
                    onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
                  >
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="low">Baixa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-gradient-to-r from-[#5AD4EB] to-[#00A7E1] text-[#004E8A] font-semibold hover:opacity-90" data-testid="save-task-btn">
                {editingTask ? "Salvar" : "Criar Tarefa"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Assistant Dialog */}
      <Dialog open={aiAssistantOpen} onOpenChange={(open) => {
        if (!open && aiStep !== "processing") {
          resetAiAssistant();
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="heading-font flex items-center gap-2">
              <Brain className="h-5 w-5 text-accent" />
              Assistente de IA para Criação de Tarefas
            </DialogTitle>
            <DialogDescription>
              Envie transcrições, áudios, atas ou anotações. A IA vai analisar e sugerir tarefas para você validar.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4">
            {/* Step 1: Upload */}
            {aiStep === "upload" && (
              <div className="space-y-6">
                {/* Upload Area */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <FileUp className="h-4 w-4" />
                    Arquivos
                  </Label>
                  <div
                    className="upload-dropzone rounded-lg p-6 text-center cursor-pointer border-2 border-dashed hover:border-accent transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="ai-upload-zone"
                  >
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="mt-2 font-medium">Clique para selecionar arquivos</p>
                    <p className="text-sm text-muted-foreground">
                      Áudios de reunião, WhatsApp, transcrições, atas, prints
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFilesSelected}
                      accept="audio/*,.ogg,.opus,.oga,.mp3,.wav,.m4a,.webm,.flac,.aac,.amr,image/*,.pdf,.doc,.docx,.txt,.md"
                      data-testid="ai-file-input"
                    />
                  </div>

                  {/* Pending Files List */}
                  {pendingFiles.length > 0 && (
                    <div className="space-y-2 max-h-40 overflow-auto">
                      {pendingFiles.map(file => (
                        <div key={file.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg group">
                          <div className={`p-2 rounded ${file.type === 'audio' ? 'bg-purple-100 text-purple-600' : file.type === 'image' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                            {getFileIcon(file.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {file.type === 'audio' ? 'Será transcrito automaticamente' : file.type}
                            </p>
                          </div>
                          {getFileStatusIcon(file.status)}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => removeFile(file.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Additional Notes */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Notas e Observações Adicionais
                  </Label>
                  <Textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder="Cole aqui anotações de reunião, mensagens de WhatsApp, briefings, contexto do projeto..."
                    className="min-h-[150px] resize-none"
                    data-testid="ai-notes-input"
                  />
                </div>

                {/* Existing Transcriptions */}
                {files.filter(f => f.transcription).length > 0 && (
                  <div className="p-4 bg-accent/10 rounded-lg">
                    <p className="text-sm flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-accent" />
                      <span className="font-medium">{files.filter(f => f.transcription).length} arquivo(s)</span>
                      <span className="text-muted-foreground">já transcritos neste projeto serão incluídos na análise</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Processing */}
            {aiStep === "processing" && (
              <div className="space-y-6 py-8">
                <div className="text-center space-y-4">
                  <div className="relative mx-auto w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-4 border-accent/20"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-accent border-t-transparent animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Brain className="h-8 w-8 text-accent" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{processingStatus.message}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Passo {processingStatus.current} de {processingStatus.total}
                    </p>
                  </div>
                </div>
                <Progress value={(processingStatus.current / processingStatus.total) * 100} className="h-2" />
                
                {/* File Processing Status */}
                <div className="space-y-2 max-h-40 overflow-auto">
                  {pendingFiles.map(file => (
                    <div key={file.id} className="flex items-center gap-3 p-2 rounded-lg">
                      {getFileIcon(file.type)}
                      <span className="flex-1 text-sm truncate">{file.name}</span>
                      {getFileStatusIcon(file.status)}
                      <span className="text-xs text-muted-foreground">
                        {file.status === 'pending' && 'Aguardando'}
                        {file.status === 'uploading' && 'Enviando...'}
                        {file.status === 'uploaded' && 'Enviado'}
                        {file.status === 'transcribing' && 'Transcrevendo...'}
                        {file.status === 'transcribed' && 'Transcrito'}
                        {file.status === 'error' && 'Erro'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {aiStep === "review" && aiResult && (
              <div className="space-y-6">
                {/* Summary */}
                {aiResult.summary && (
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Resumo da Análise</Label>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm">{aiResult.summary}</p>
                    </div>
                  </div>
                )}

                {/* Key Points */}
                {aiResult.key_points?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Pontos-Chave Identificados</Label>
                    <ul className="space-y-1">
                      {aiResult.key_points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Suggested Tasks */}
                {aiResult.suggested_tasks?.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Tarefas Sugeridas</Label>
                      <span className="text-sm text-muted-foreground">
                        {selectedTasks.length} de {aiResult.suggested_tasks.length} selecionadas
                      </span>
                    </div>
                    <div className="space-y-2 max-h-[300px] overflow-auto pr-2">
                      {aiResult.suggested_tasks.map((task, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedTasks.includes(index)
                              ? 'border-accent bg-accent/5'
                              : 'border-border hover:border-accent/50'
                          }`}
                          onClick={() => toggleTaskSelection(index)}
                          data-testid={`suggested-task-${index}`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedTasks.includes(index)}
                              onCheckedChange={() => toggleTaskSelection(index)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{task.title}</h4>
                                {task.priority && (
                                  <Badge className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>
                                    {PRIORITY_LABELS[task.priority]}
                                  </Badge>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!aiResult.suggested_tasks || aiResult.suggested_tasks.length === 0) && (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground/50" />
                    <p className="mt-2 text-muted-foreground">Nenhuma tarefa sugerida pela IA</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            {aiStep === "upload" && (
              <>
                <Button variant="outline" onClick={resetAiAssistant}>
                  Cancelar
                </Button>
                <Button
                  onClick={processFilesAndAnalyze}
                  disabled={pendingFiles.length === 0 && !additionalNotes.trim()}
                  className="bg-gradient-to-r from-[#004E8A] to-[#00A7E1] text-white font-semibold hover:opacity-90"
                  data-testid="ai-analyze-btn"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Analisar com IA
                </Button>
              </>
            )}
            {aiStep === "review" && (
              <>
                <Button variant="outline" onClick={() => setAiStep("upload")}>
                  Voltar
                </Button>
                <Button
                  onClick={createSelectedTasks}
                  disabled={selectedTasks.length === 0}
                  className="bg-gradient-to-r from-[#5AD4EB] to-[#00A7E1] text-[#004E8A] font-semibold hover:opacity-90"
                  data-testid="ai-create-tasks-btn"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Criar {selectedTasks.length} Tarefa(s)
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
